import { INBOUND_STRINGS, QUALITY_THRESHOLDS, QUALITY_ISSUE_LABELS } from "../../util/Constants";

/**
 * Transform API responses into the format expected by UI components
 * Combines PPH and Downtime data to create the complete station performance data
 */

/**
 * Maps legacy/short timezone codes to IANA timezone strings.
 * Mirrors the OculusAssetsWebsite oldTimeZoneMapper pattern.
 * @param {string} timezone
 * @returns {string} IANA timezone string
 */
export function normalizeTimezone(timezone) {
    if (!timezone) return undefined;
    switch (timezone.trim()) {
        case "US/Eastern":   return "America/New_York";
        case "US/Central":   return "America/Chicago";
        case "US/Pacific":   return "America/Los_Angeles";
        case "US/Mountain":  return "America/Denver";
        case "US/Arizona":   return "America/Phoenix";
        case "IST":          return "Asia/Kolkata";
        case "JST":          return "Asia/Tokyo";
        default:             return timezone.trim();
    }
}

/**
 * Convert a local date+time string (YYYY-MM-DD, HH:MM) entered in station timezone
 * to UTC epoch milliseconds.
 * @param {string} dateStr  - "YYYY-MM-DD"
 * @param {string} timeStr  - "HH:MM"
 * @param {string} stationTimezone - IANA timezone string (e.g. "America/New_York")
 * @returns {number} epoch milliseconds in UTC
 */
export function localStationTimeToEpochMs(dateStr, timeStr, stationTimezone) {
    const ianaTimezone = normalizeTimezone(stationTimezone);
    if (!ianaTimezone) {
        // Fallback: treat as UTC if no timezone available
        return new Date(`${dateStr}T${timeStr}:00Z`).getTime();
    }
    const localDateTimeStr = `${dateStr}T${timeStr}:00`;
    const naiveUtcMs = new Date(`${localDateTimeStr}Z`).getTime();
    // Two-pass offset lookup to handle DST transitions correctly:
    // first pass gets an approximation, second pass corrects it using the closer UTC reference.
    const approxUtcMs = naiveUtcMs - getTimezoneOffsetMs(new Date(naiveUtcMs), ianaTimezone);
    return naiveUtcMs - getTimezoneOffsetMs(new Date(approxUtcMs), ianaTimezone);
}

/**
 * Returns the UTC offset in milliseconds for a given timezone at a given date.
 * Positive = ahead of UTC (e.g. Asia/Kolkata = +5:30 = +19800000)
 * @param {Date} date
 * @param {string} ianaTimezone
 * @returns {number} offset in ms
 */
function getTimezoneOffsetMs(date, ianaTimezone) {
    // Format the date in the target timezone using ja locale (gives unambiguous numeric parts)
    const parts = date.toLocaleString("ja", { timeZone: ianaTimezone }).split(/[/\s:]/);
    parts[1]--; // month is 1-based in ja locale output
    const localAsUtcMs = Date.UTC(parts[0], parts[1], parts[2], parts[3], parts[4], parts[5]);
    return localAsUtcMs - date.getTime();
}

/**
 * Format an ISO 8601 UTC timestamp to HH:MM in station timezone.
 * Falls back to browser timezone if stationTimezone is not provided.
 * @param {string} timestamp - ISO 8601 timestamp (e.g., "2026-01-25T22:30:00.000Z")
 * @param {string} [stationTimezone] - IANA timezone string
 * @returns {string} Formatted time (e.g., "22:30")
 */
export function formatTimestampInStationTz(timestamp, stationTimezone) {
    if (!timestamp || !timestamp.includes('T')) return timestamp;
    const ianaTimezone = normalizeTimezone(stationTimezone);
    const date = new Date(timestamp);
    let result;
    if (ianaTimezone) {
        const parts = date.toLocaleString("en-US", {
            timeZone: ianaTimezone,
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
        });
        result = parts.replace(/^24:/, "00:");
    } else {
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        result = `${hours}:${minutes}`;
    }
    return result;
}

/**
 * Format an ISO 8601 UTC timestamp to MM/DD in station timezone.
 * @param {string} timestamp
 * @param {string} [stationTimezone]
 * @returns {string} e.g. "01/25"
 */
function formatDateInStationTz(timestamp, stationTimezone) {
    const ianaTimezone = normalizeTimezone(stationTimezone);
    const date = new Date(timestamp);
    if (ianaTimezone) {
        const month = date.toLocaleString("en-US", { timeZone: ianaTimezone, month: "2-digit" });
        const day   = date.toLocaleString("en-US", { timeZone: ianaTimezone, day: "2-digit" });
        return `${month}/${day}`;
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day   = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}`;
}

/**
 * Transform PPH API response
 * API shape: { stationCode, stationPPH, beltMetricsMap, pphDataPoints: [{ dataPointList: [{ metricValue, timeStampVal }] }] }
 * @param {Object} pphData - Response from getInboundPPHMetric API
 * @param {string} stationTimezone - IANA timezone string from filter context (fetched via getFilters on page load)
 * @returns {Object} Transformed PPH data
 */
export function transformPPHData(pphData, stationTimezone) {
    if (!pphData) return null;

    const stationCode = pphData.stationCode || pphData.nodeId;
    const { stationPPH, beltMetricsMap } = pphData;

    let trendData = [];
    if (pphData.pphDataPoints && pphData.pphDataPoints.length > 0) {
        const dataPointList = pphData.pphDataPoints[0].dataPointList || [];

        // Pre-compute date and time for each point
        const points = dataPointList.map(item => ({
            time: formatTimestampInStationTz(item.timeStampVal, stationTimezone),
            date: formatDateInStationTz(item.timeStampVal, stationTimezone),
            actual: item.metricValue,
            expected: stationPPH
        }));

        trendData = points;
    }

    return {
        stationCode,
        stationPPH,
        totalPackages: pphData.totalPackages || 0,
        stationTimezone,
        trendData,
        beltMetricsMap: beltMetricsMap || {}
    };
}

/**
 * Transform Downtime API response
 * API shape: { stationCode, totalDPMO, totalDowntimeHours, totalDowntimeInstances, totalOpportunities, beltDowntimeMap }
 * beltDowntimeMap entry: { downtimeInstances, dpmo, lostHours, totalOpportunities }
 * @param {Object} downtimeData - Response from getInboundDowntimeMetric API
 * @returns {Object} Transformed downtime data
 */
export function transformDowntimeData(downtimeData) {
    if (!downtimeData) return null;

    const stationCode = downtimeData.stationCode || downtimeData.nodeId;
    const beltDowntimeMap = downtimeData.beltDowntimeMap || {};
    const { totalDPMO, totalDowntimeHours, totalDowntimeInstances, totalOpportunities } = downtimeData;

    const beltDowntime = Object.entries(beltDowntimeMap).map(([beltId, metrics]) => ({
        name: beltId,
        hours: metrics.lostHours,
        dpmo: metrics.dpmo,
        instances: metrics.downtimeInstances,
        opportunities: metrics.totalOpportunities
    }));

    const topImpactedLines = [...beltDowntime]
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 3);

    const topVolumeImpact = [...beltDowntime]
        .sort((a, b) => b.dpmo - a.dpmo)
        .slice(0, 3)
        .map(line => ({
            name: line.name,
            dpmo: line.dpmo,
            packages: line.opportunities
        }));

    return {
        stationCode,
        totalDPMO,
        totalDowntimeHours,
        totalDowntimeInstances,
        totalOpportunities,
        topImpactedLines,
        topVolumeImpact,
        beltDowntimeMap
    };
}

/**
 * Combine PPH and Downtime belt data into operating lines
 * @param {Object} pphData - Transformed PPH data
 * @param {Object} downtimeData - Transformed downtime data
 * @param {Object} [qualityData] - Transformed quality data (optional)
 * @returns {Array} Operating lines data for OperatingLines component
 */
export function combineOperatingLinesData(pphData, downtimeData, qualityData) {
    if (!pphData || !downtimeData) return [];

    const { beltMetricsMap } = pphData;
    const { beltDowntimeMap } = downtimeData;
    const beltQualityMap = (qualityData && qualityData.beltQualityMap) || {};

    const beltIds = new Set([
        ...Object.keys(beltMetricsMap),
        ...Object.keys(beltDowntimeMap)
    ]);

    const operatingLines = Array.from(beltIds).map(beltId => {
        const pphMetrics = beltMetricsMap[beltId] || { pph: 0, packageCount: 0 };
        const downtimeMetrics = beltDowntimeMap[beltId] || { lostHours: 0, dpmo: 0 };

        const actual = pphMetrics.pph;
        const packageCount = pphMetrics.packageCount || 0;
        const inductedCube = pphMetrics.avgVolumeFt3 || 0;
        const benchmark = (pphMetrics.benchmarkPph > 0) ? pphMetrics.benchmarkPph : 1400;
        const performancePercentage = benchmark > 0 ? parseFloat(((actual / benchmark) * 100).toFixed(1)) : 0;

        // FPH impact: hours lost due to running below benchmark
        // Formula: packageCount * ((benchmark - actual) / (benchmark * actual))
        const fphImpact = (benchmark > 0 && actual > 0)
            ? parseFloat((packageCount * ((benchmark - actual) / (benchmark * actual))).toFixed(2))
            : 0;

        const lineData = {
            name: beltId,
            lostHours: downtimeMetrics.lostHours,
            actual,
            packageCount,
            inductedCube,
            inductDowntimeDpmo: downtimeMetrics.dpmo,
            benchmark,
            performancePercentage,
            fphImpact,
            showBenchmark: performancePercentage < 100,
            benchmarkPosition: 100,
        };

        // Attach quality metrics for lines that have quality data
        if (beltQualityMap[beltId]) {
            lineData.qualityMetrics = beltQualityMap[beltId];
        }

        return lineData;
    });

    return operatingLines.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
}

/**
 * Transform complete station performance data for UI
 * @param {Object} pphData - Response from getInboundPPHMetric API
 * @param {Object} downtimeData - Response from getInboundDowntimeMetric API
 * @param {string} stationTimezone - IANA timezone string from filter context
 * @param {Object} [qualityData] - Response from GetMetricData quality API (optional, null when flag disabled)
 * @returns {Object} Complete station performance data
 */
export function transformStationPerformanceData(pphData, downtimeData, stationTimezone, qualityData) {
    const transformedPPH = transformPPHData(pphData, stationTimezone);
    const transformedDowntime = transformDowntimeData(downtimeData);
    const transformedQuality = transformQualityMetricsData(qualityData);

    if (!transformedPPH || !transformedDowntime) {
        console.warn("[DataTransformer] Missing data - PPH or Downtime is null");
        return null;
    }

    const operatingLines = combineOperatingLinesData(transformedPPH, transformedDowntime, transformedQuality);

    // Top 3 impacted lines by FPH impact (highest impact first)
    // Sorted by: packageCount * ((benchmark - actual) / (benchmark * actual))
    // Displayed using performancePercentage (actual/benchmark * 100)
    const bottomLines = [...operatingLines]
        .sort((a, b) => b.fphImpact - a.fphImpact)
        .slice(0, 3)
        .map(line => ({
            name: line.name,
            pph: line.actual,
            percentage: line.performancePercentage
        }));

    const result = {
        stationTimezone: transformedPPH.stationTimezone,
        totalFPH: {
            value: transformedPPH.stationPPH,
            totalPackages: transformedPPH.totalPackages,
            subtitle: INBOUND_STRINGS.SUBTITLE_PACKAGES_PER_HOUR,
            tooltip: INBOUND_STRINGS.TOOLTIP_TOTAL_FPH,
            trendData: transformedPPH.trendData,
            bottomLines
        },
        lostHours: {
            value: transformedDowntime.totalDowntimeHours,
            subtitle: INBOUND_STRINGS.SUBTITLE_CUMULATIVE_DOWNTIME,
            tooltip: INBOUND_STRINGS.TOOLTIP_LOST_HOURS,
            topImpactedLines: transformedDowntime.topImpactedLines
        },
        inductDTDPMO: {
            value: transformedDowntime.totalDPMO,
            subtitle: INBOUND_STRINGS.SUBTITLE_DEFECTS_PER_MILLION,
            tooltip: INBOUND_STRINGS.TOOLTIP_INDUCT_DT_DPMO,
            topVolumeImpact: transformedDowntime.topVolumeImpact
        },
        qualityMetrics: {
            tooltip: INBOUND_STRINGS.TOOLTIP_QUALITY_METRICS,
            metrics: transformedQuality ? transformedQuality.metrics : [],
            topImpact: transformedQuality ? transformedQuality.topImpact : [],
            isEnabled: transformedQuality !== null
        },
        operatingLines
    };

    return result;
}


/**
 * Transform quality metrics API response (UTRMetricsCommonModel format) into UI format.
 * Handles the GetMetricData response with metricResults containing NO_READ_PCT, MULTI_READ_PCT, MISAPPLIED_SAL_PCT.
 *
 * Each metricResult has metricValues array with:
 *   - { name: "RATE", unit: "PERCENT", value: <rate> }
 *   - { name: "NUMERATOR", unit: "COUNT", value: <count> }
 *   - { name: "DENOMINATOR", unit: "COUNT", value: <count> }
 *   - { name: "PER_BELT", unit: "PERCENT", values: { default: [{ name, displayName, value }] } } (optional)
 *
 * @param {Object|null} qualityData - Response from GetMetricData API, or null if feature disabled
 * @returns {Object|null} Transformed quality metrics data, or null if no data
 */
export function transformQualityMetricsData(qualityData) {
    if (!qualityData) return null;

    const metricResults = qualityData.metricResults || [];
    if (metricResults.length === 0) return null;

    const metrics = [];
    const beltQualityMap = {};
    const metricNameToKey = {
        'NO_READ_PCT': 'noReadRate',
        'MULTI_READ_PCT': 'multiReadRate',
        'MISAPPLIED_SAL_PCT': 'misappliedSalRate'
    };
    const metricNameToLabel = {
        'NO_READ_PCT': QUALITY_ISSUE_LABELS.NO_READ,
        'MULTI_READ_PCT': QUALITY_ISSUE_LABELS.MULTI_READ,
        'MISAPPLIED_SAL_PCT': QUALITY_ISSUE_LABELS.MISAPPLIED_SAL
    };
    const metricNameToMessageId = {
        'NO_READ_PCT': 'inbound.no_read',
        'MULTI_READ_PCT': 'inbound.multi_read',
        'MISAPPLIED_SAL_PCT': 'inbound.misapplied_sal'
    };
    const metricThresholds = {
        'NO_READ_PCT': { critical: QUALITY_THRESHOLDS.NO_READ.critical },
        'MULTI_READ_PCT': { critical: QUALITY_THRESHOLDS.MULTI_READ.critical },
        'MISAPPLIED_SAL_PCT': { critical: QUALITY_THRESHOLDS.MISAPPLIED_SAL.critical }
    };

    metricResults.forEach(result => {
        const metricName = result.metric && result.metric.metricName;
        if (!metricName || !metricNameToKey[metricName]) return;

        // Skip metrics with errors
        if (result.errorCode) return;

        const metricValues = result.metricValues || [];
        const rateEntry = metricValues.find(v => v.name === 'RATE');
        const numeratorEntry = metricValues.find(v => v.name === 'NUMERATOR');
        const denominatorEntry = metricValues.find(v => v.name === 'DENOMINATOR');
        const perBeltEntry = metricValues.find(v => v.name === 'PER_BELT');

        // Check if there's no data (metadata flag or zero denominator)
        const noData = (result.metadata && result.metadata.additionalInfo && result.metadata.additionalInfo.no_data === 'true');
        const rate = rateEntry ? rateEntry.value : null;
        const numerator = numeratorEntry ? numeratorEntry.value : 0;
        const denominator = denominatorEntry ? denominatorEntry.value : 0;

        // Determine display value — null rate means no traffic, show "—"
        const displayValue = (rate === null || noData || denominator === 0) ? null : rate;
        const thresholds = metricThresholds[metricName];
        let status = 'neutral';
        if (displayValue !== null) {
            status = displayValue > thresholds.critical ? 'critical' : 'positive';
        }

        metrics.push({
            label: metricNameToLabel[metricName],
            messageId: metricNameToMessageId[metricName],
            value: displayValue,
            numerator,
            denominator,
            status,
            metricName
        });

        // Process per-belt breakdown
        if (perBeltEntry && perBeltEntry.values) {
            // UTRMetricsCommonModel TableMetricValue: values is a list of TableColumnData
            // Handle both formats: direct array or map with "default" key (for backward compat)
            const beltList = Array.isArray(perBeltEntry.values)
                ? perBeltEntry.values
                : (perBeltEntry.values.default || []);
            beltList.forEach(beltEntry => {
                const beltKey = beltEntry.name;
                if (!beltQualityMap[beltKey]) {
                    beltQualityMap[beltKey] = {
                        misappliedSalRate: null,
                        multiReadRate: null,
                        noReadRate: null
                    };
                }
                const rateKey = metricNameToKey[metricName];
                beltQualityMap[beltKey][rateKey] = beltEntry.value != null ? beltEntry.value : 0;
            });
        }
    });

    // If no valid metrics were processed, treat as no data
    if (metrics.length === 0) return null;

    // Build top impact: for each metric type, find the belt with the highest rate
    const topImpact = [];
    const beltEntries = Object.entries(beltQualityMap);

    if (beltEntries.length > 0) {
        // Misapplied SAL — belt with highest rate
        const topMisapplied = beltEntries
            .map(([name, m]) => ({ name, rate: m.misappliedSalRate || 0 }))
            .sort((a, b) => b.rate - a.rate)[0];
        if (topMisapplied && topMisapplied.rate > 0) {
            topImpact.push({ issue: QUALITY_ISSUE_LABELS.MISAPPLIED_SAL, name: topMisapplied.name, rate: topMisapplied.rate });
        }

        // Multi-read — belt with highest rate
        const topMultiRead = beltEntries
            .map(([name, m]) => ({ name, rate: m.multiReadRate || 0 }))
            .sort((a, b) => b.rate - a.rate)[0];
        if (topMultiRead && topMultiRead.rate > 0) {
            topImpact.push({ issue: QUALITY_ISSUE_LABELS.MULTI_READ, name: topMultiRead.name, rate: topMultiRead.rate });
        }

        // No-read — belt with highest rate
        const topNoRead = beltEntries
            .map(([name, m]) => ({ name, rate: m.noReadRate || 0 }))
            .sort((a, b) => b.rate - a.rate)[0];
        if (topNoRead && topNoRead.rate > 0) {
            topImpact.push({ issue: QUALITY_ISSUE_LABELS.NO_READ, name: topNoRead.name, rate: topNoRead.rate });
        }
    }

    return {
        metrics,
        topImpact,
        beltQualityMap
    };
}



// WEBPACK FOOTER //
// ./src/components/inbound/utils/dataTransformer.js